import { useEffect, useState, useCallback } from "react";
import { listFiles, renameFile, deleteFile } from "../../utils/driveClient";
import { toast } from "react-toastify";
import FileRow from "./FileRow";
import UploadDropzone from "./UploadDropzone";
import { useActionDialog } from "../ActionDialog";

export default function FileList({ folderId, onSelect, onNavigateInto }) {
  const { confirm, prompt, dialog } = useActionDialog();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listFiles(folderId);
      setFiles(result);
    } catch (e) {
      toast.error(`Failed to load files: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [folderId]);

  useEffect(() => { reload(); }, [reload]);

  const handleClick = (file) => {
    if (file.mimeType === "application/vnd.google-apps.folder") {
      onNavigateInto(file);
    } else {
      onSelect(file);
    }
  };

  const handleRename = async (file) => {
    const newName = await prompt({
      title: "Rename File",
      label: "New name",
      defaultValue: file.name,
      confirmLabel: "Rename",
    });
    if (!newName || newName === file.name) return;
    try {
      await renameFile(file.id, newName);
      toast.success("Renamed");
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (file) => {
    const ok = await confirm({
      title: "Delete file",
      body: `Delete ${file.name}?`,
      tone: "danger",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await deleteFile(file.id);
      toast.success("Deleted");
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const handleDownload = (file) => {
    // webViewLink isn't a direct download, but it's good enough; Drive will
    // prompt download for non-Google file types via &export=download
    window.open(file.webViewLink, "_blank");
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-3">
        <UploadDropzone folderId={folderId} onUploaded={reload} />
      </div>
      <div className="grid grid-cols-[1fr,90px,80px,40px] gap-2 px-3 py-2 text-xs text-gray-500 font-medium uppercase border-b">
        <span>Name</span><span>Modified</span><span>Size</span><span />
      </div>
      {loading
        ? <div className="p-8 text-center text-gray-500">Loading…</div>
        : files.length === 0
          ? <div className="p-8 text-center text-gray-500">Empty folder</div>
          : files.map(f => (
              <FileRow
                key={f.id}
                file={f}
                onClick={handleClick}
                onRename={handleRename}
                onMove={() => toast.info("Move: drag a file onto a subfolder, or use Open in Drive for now")}
                onDelete={handleDelete}
                onDownload={handleDownload}
              />
            ))}
      {dialog}
    </div>
  );
}
